"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import {
  ArrowRight,
  Bell,
  Headphones,
  History,
  Maximize,
  RefreshCw,
  Search,
  Settings,
  User,
} from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const MENU = [
  ["Home", "Dashboard (five tabs)"],
  ["Banking", "Banks, Bank Transfer Logs"],
  [
    "All Master",
    "Item, Quality, Division, Branch, Warehouse, Process, Recipe, Price List, Item Group, ManPower, Sales Order Tag",
  ],
  ["Production", "Add Production, Submit Production"],
  [
    "Sales",
    "Customer, Quotes, Customer Orders, Sales Orders, Delivery Challans, Invoices, Payments Received, Recurring Invoices, Credit Notes",
  ],
  [
    "Purchase",
    "Vendors, Expenses, Recurring Expenses, Purchase Orders, GRPO, Bills, Payments Made, Recurring Bills, Debit Notes",
  ],
  [
    "Accountant",
    "Chart of Accounts, Account Ledger, Manual Journals, Account Payment Received, Account Payment Made",
  ],
  ["Inventory", "View Inventory, Inventory Transfer, Inventory Adjustment"],
  ["Reports", "Opens Reports Overview"],
  ["Analytics", "Opens Analytics Dashboard (28 named analytics)"],
];

const HEADER = [
  { Icon: RefreshCw, label: "Sync data", desc: "Reloads the page" },
  { Icon: Maximize, label: "Full screen", desc: "Focus mode" },
  { Icon: History, label: "Recent Updates", desc: "Product notes" },
  {
    Icon: Search,
    label: "Search · ⌘K",
    desc: "Quick Navigation — jump to a page you can open, not a customer or invoice lookup",
  },
  { Icon: Bell, label: "Notifications", desc: "In-app / push bell" },
  {
    Icon: Headphones,
    label: "Help & Support",
    desc: "Support tickets",
  },
  { Icon: Settings, label: "Settings", desc: "Gear — not on the left menu" },
  {
    Icon: User,
    label: "Avatar",
    desc: "Name, email, role, My account (accounts.abss.ai), Sign out",
  },
];

const SETTINGS_GROUPS = [
  { name: "My account", items: "My profile" },
  {
    name: "Organization",
    items: "Profile, Currencies, WhatsApp config, GST portal credentials, Manage subscription",
  },
  { name: "Users and roles", items: "Users, Roles" },
  {
    name: "Sales",
    items: "Quotation config, Sales target (Incentive schemes if on plan)",
  },
  {
    name: "Inventory",
    items: "Inventory config",
  },
  {
    name: "Preferences and advanced",
    items: "Additional charges, Discounts, Credit limits, Organization defaults, Notifications",
  },
];

const DASH_TABS = [
  "General Dashboard",
  "Sales Dashboard",
  "Credit Limit Dashboard",
  "Individual Work Tracking",
  "Getting Started",
];

const sections = [
  { heading: "Left menu" },
  { heading: "Header" },
  { heading: "Settings" },
  { heading: "Home tabs" },
];

export default function FindingYourWayPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Finding Your Way Around
          </h1>
          <h2 className="text-[20px] sentient-medium text-[#111827] mb-3">
            For: Anyone opening Books for the first time
          </h2>
          <p className="text-[16px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Chrome of the app — menu, header, Settings. What each module{" "}
            <em>does</em> is on its own Help Center page.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            Left menu
          </h2>
          <p className="text-[15px] geist-regular text-[#374151] mb-4">
            Bottom of the sidebar: your name and a Support icon (same tickets as
            the header headphones). Support is not a menu category.
          </p>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[22%]">
                    Category
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Screens
                  </th>
                </tr>
              </thead>
              <tbody>
                {MENU.map(([cat, inside]) => (
                  <tr
                    key={cat}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {cat}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {inside}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Header
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {HEADER.map((h) => {
              const Icon = h.Icon;
              return (
                <div
                  key={h.label}
                  className="border border-[#E5E5E5] rounded-[12px] p-4 bg-white flex gap-3"
                >
                  <div className="w-8 h-8 rounded-[8px] bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#111827]" />
                  </div>
                  <div>
                    <p className="text-[14px] geist-medium text-[#111827]">
                      {h.label}
                    </p>
                    <p className="text-[13px] geist-regular text-[#374151]">
                      {h.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <Callout type="note">
            Title of the shortcut modal is <B>Quick Navigation</B>. Placeholder:
            Search pages, e.g. quotation, sales order, vendors... Empty: No
            pages match your search. Company switcher sits left of the avatar
            if you belong to more than one company.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-2"
            className="text-[24px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            Settings
          </h2>
          <p className="text-[15px] geist-regular text-[#374151] mb-4">
            Header gear → All settings. Groups on screen:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SETTINGS_GROUPS.map((g) => (
              <div
                key={g.name}
                className="border border-[#E5E5E5] rounded-[12px] p-4 bg-white"
              >
                <p className="text-[14px] geist-medium text-[#111827] mb-1">
                  {g.name}
                </p>
                <p className="text-[13px] geist-regular text-[#374151]">
                  {g.items}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-3"
            className="text-[24px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            Home tabs
          </h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {DASH_TABS.map((t) => (
              <span
                key={t}
                className="px-3 py-1.5 text-[13px] geist-medium text-[#111827] border border-[#E5E5E5] rounded-full bg-white"
              >
                {t}
              </span>
            ))}
          </div>
          <p className="text-[15px] geist-regular text-[#374151]">
            Default is General Dashboard. Full cards, filters, and rules are on
            the Dashboard page — not repeated here.
          </p>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/home/dashboard"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Dashboard <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              What each Home tab is for
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/my-profile"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              My Profile <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              First Settings screen
            </p>
          </Link>
          <Link
            href="/documentation/books/support/support"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Support <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Header Help & Support
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
